/**
 * Phone OTP verification UI — used from Profile when REACT_APP_ENABLE_PHONE_OTP=true.
 * Renders as an embedded auth card (no page chrome) so it fits inside Profile.
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui";
import logo from "../assets/logos/logo.png";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./AuthPageLayout.css";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

function VerifyPhoneOtp({ phone: phoneProp, onVerified, onBack }) {
  const { t } = useTranslation("translation");
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [phone, setPhone] = useState(phoneProp || "");
  const inputsRef = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const sendOtp = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.post(
        "/phone/send-otp",
        { phone: phone || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.phone) setPhone(res.data.phone);
      setCooldown(RESEND_SECONDS);
      toast.info(
        t("phoneOtp.sent", {
          defaultValue: "Verification code generated. SMS delivery is not configured yet.",
        })
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          t("phoneOtp.sendFailed", { defaultValue: "Failed to send code." })
      );
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (index, value) => {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < text.length; i += 1) next[i] = text[i];
    setDigits(next);
    inputsRef.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleConfirm = async () => {
    const otp = digits.join("");
    if (otp.length !== OTP_LENGTH) {
      toast.error(t("phoneOtp.incomplete", { defaultValue: "Enter the 6-digit code." }));
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await api.post(
        "/phone/verify-otp",
        { otp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        t("phoneOtp.verified", { defaultValue: "Phone number verified successfully." })
      );
      if (onVerified) onVerified();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          t("phoneOtp.verifyFailed", { defaultValue: "Invalid verification code." })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-embedded-card">
      <div className="auth-page-card__logo">
        <img src={logo} alt="Loops" />
      </div>
      <h1 className="auth-page-card__title">
        {t("phoneOtp.title", { defaultValue: "Verify your phone number" })}
      </h1>
      <p className="auth-page-card__subtitle">
        {t("phoneOtp.sentTo", {
          phone: phone || "—",
          defaultValue: `We sent a 6-digit code to ${phone || "your phone"}`,
        })}
      </p>

      <div className="auth-otp-inputs" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            className="auth-otp-digit"
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={d}
            aria-label={t("phoneOtp.digitLabel", {
              n: i + 1,
              defaultValue: `Digit ${i + 1}`,
            })}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>

      <Button
        color="primary"
        size="md"
        className="auth-submit"
        onClick={handleConfirm}
        disabled={loading}
      >
        {loading
          ? t("phoneOtp.confirming", { defaultValue: "Confirming..." })
          : t("phoneOtp.confirm", { defaultValue: "CONFIRM" })}
      </Button>

      <p className="auth-otp-hint">
        {cooldown > 0 ? (
          t("phoneOtp.resendIn", {
            seconds: cooldown,
            defaultValue: `Didn't receive a code? Resend in ${cooldown}s`,
          })
        ) : (
          <button type="button" disabled={sending} onClick={sendOtp}>
            {t("phoneOtp.resend", { defaultValue: "Resend code" })}
          </button>
        )}
      </p>

      {onBack ? (
        <button type="button" className="auth-otp-back" onClick={onBack}>
          ← {t("phoneOtp.back", { defaultValue: "Back" })}
        </button>
      ) : null}
    </div>
  );
}

export default VerifyPhoneOtp;
